import db, {
  createPaymentIntent,
  confirmAuthorizationCompletion,
} from "./utils/firebase";
import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  trackCheckoutStep,
  trackPaymentError,
  trackPaymentSuccess,
  trackFormInteraction,
  trackPerformanceMetric,
} from "./utils/analytics";
import {
  validateEmail,
  validateName,
  validateAmount,
  validateTourId,
  sanitizeInput,
  createNonce,
  validateOrigin,
  secureErrorLog,
} from "./utils/validation";
import { collection, getDocs } from "firebase/firestore";
import { CardElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { getQueryVariable, getLiveMode } from "./App";
import { SubmitButton } from "./SubmitButton";
import { Field, CardField } from "./CARD_OPTIONS";
import { ErrorMessage } from "./ErrorMessage";
import { ErrorPopup } from "./ErrorPopup";
import { ResetButton } from "./ResetButton";
import { LoadingIndicator } from "./LoadingIndicator";

const getErrorMessage = (error) => {
  const commonErrors = {
    card_declined: "Your card was declined. Please try another card.",
    expired_card: "Your card has expired. Please use a different card.",
    incorrect_cvc:
      "The security code (CVC) is incorrect. Please check and try again.",
    insufficient_funds:
      "Your card has insufficient funds. Please use a different card.",
    invalid_expiry_year:
      "The expiration year is invalid. Please check and try again.",
    invalid_expiry_month:
      "The expiration month is invalid. Please check and try again.",
    invalid_number: "The card number is invalid. Please check and try again.",
    processing_error:
      "An error occurred while processing your card. Please try again.",
  };

  if (error.code && commonErrors[error.code]) {
    return commonErrors[error.code];
  }

  if (error.type === "card_error" || error.type === "validation_error") {
    return error.message;
  }

  return "An unexpected error occurred. Please try again. If the problem persists, please contact support.";
};

const TourPreview = ({ tourImagePath, tourName }) => {
  const [hasImageError, setHasImageError] = useState(false);

  if (!tourImagePath || hasImageError) {
    return (
      <div className="TourPreviewPlaceholder">
        <div className="TourPreviewPlaceholderIcon">GH</div>
        <div className="TourPreviewPlaceholderText">
          <strong>{tourName || "Private tour"}</strong>
          <span>Tour image unavailable</span>
        </div>
      </div>
    );
  }

  return (
    <img
      src={tourImagePath}
      alt={`${tourName} tour`}
      className="TourImage"
      onError={() => setHasImageError(true)}
    />
  );
};

const CheckoutHero = ({ nameForTour, priceForTour, tourImagePath }) => (
  <section className="PaymentHeroCard">
    <div className="HeroTopline">
      <span className="HeroEyebrow">Private tour checkout</span>
      <div className="HeroTrustPill">Card payment</div>
    </div>
    <img className="LogoImg" src="logo_poziom2_b.png" alt="GuideHub logo" />
    <div className="HeroHeadlineBlock">
      <h1 className="PaymentTitle">Confirm your booking</h1>
      <p className="PaymentIntro">
        Add your contact details, pay securely, and open the booking right
        after the payment is approved.
      </p>
    </div>

    <div className="TourImageContainer">
      <TourPreview tourImagePath={tourImagePath} tourName={nameForTour} />
    </div>

    <div className="PaymentOverviewCard">
      <div className="PaymentOverviewHeader">
        <div>
          <div className="PaymentOverviewLabel">Selected experience</div>
          <div className="PaymentOverviewTitle">{nameForTour}</div>
        </div>
        <div className="PaymentOverviewAmount">
          <span>Total</span>
          <strong>{priceForTour} PLN</strong>
        </div>
      </div>

      <div className="PaymentSteps">
        <div className="Step">
          <span className="StepNumber">1</span>
          <span>Add your details</span>
        </div>
        <div className="Step">
          <span className="StepNumber">2</span>
          <span>Pay securely</span>
        </div>
        <div className="Step">
          <span className="StepNumber">3</span>
          <span>Open booking</span>
        </div>
      </div>

      <div className="HeroStatRow">
        <div className="HeroStat">
          <span className="HeroStatLabel">Payment method</span>
          <strong>Card checkout</strong>
        </div>
        <div className="HeroStat">
          <span className="HeroStatLabel">Confirmation</span>
          <strong>Available right after payment</strong>
        </div>
      </div>
    </div>
  </section>
);

export const CheckoutForm = () => {
  const stripe = useStripe();
  const elements = useElements();
  const didInitRef = useRef(false);

  const [error, setError] = useState(null);
  const [cardComplete, setCardComplete] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [billingDetails, setBillingDetails] = useState({
    email: "",
    phone: "",
    name: "",
  });
  const [priceForTour, setPriceForTour] = useState(null);
  const [nameForTour, setNameForTour] = useState(null);
  const [finished, setIsFinished] = useState(false);
  const [tourImagePath, setTourImagePath] = useState(null);
  const [showError, setShowError] = useState(false);
  const [nonce] = useState(createNonce());
  const [loading, setLoading] = useState(true);

  const handleError = useCallback((incomingError) => {
    const sanitizedError = secureErrorLog(incomingError);
    const errorMessage = getErrorMessage(incomingError);

    trackPaymentError(
      incomingError.step || "unknown",
      errorMessage,
      sanitizedError,
    );

    setError({
      message: errorMessage,
      detail: sanitizedError.message || "",
    });
    setShowError(true);
    setProcessing(false);
  }, []);

  const validateInputs = useCallback(() => {
    if (!validateName(billingDetails.name)) {
      handleError({
        message: "Please enter a valid name.",
        step: "input_validation",
      });
      return false;
    }

    if (!validateEmail(billingDetails.email)) {
      handleError({
        message: "Please enter a valid email address.",
        step: "input_validation",
      });
      return false;
    }

    if (!validateAmount(priceForTour)) {
      handleError({
        message: "Invalid payment amount.",
        step: "amount_validation",
      });
      return false;
    }

    const tourId = getQueryVariable("TourId");
    if (!validateTourId(tourId)) {
      handleError({
        message: "Invalid tour ID.",
        step: "tour_validation",
      });
      return false;
    }

    return true;
  }, [billingDetails.email, billingDetails.name, handleError, priceForTour]);

  const getTourInformation = useCallback(async () => {
    setLoading(true);

    try {
      const tourId = getQueryVariable("TourId");

      if (!validateTourId(tourId)) {
        handleError({
          message: "Invalid tour ID format.",
          step: "tour_validation",
        });
        return;
      }

      const querySnapshot = await getDocs(collection(db, "Tours"));
      let found = false;

      for (const tourDoc of querySnapshot.docs) {
        const tourData = tourDoc.data();
        if (tourId !== tourDoc.id && tourId !== tourData.serialId) {
          continue;
        }

        if (tourData.status !== "awaiting_payment") {
          continue;
        }

        found = true;
        setPriceForTour(tourData.price);
        setNameForTour(tourData.destination);

        try {
          const portfolioSnapshot = await getDocs(collection(db, "Portfolio"));
          for (const portfolioDoc of portfolioSnapshot.docs) {
            const portfolioData = portfolioDoc.data();
            const portfolioTourName = portfolioData.Tour || portfolioData.tour;

            if (portfolioTourName === tourData.destination) {
              setTourImagePath(
                portfolioData.imageUrl || portfolioData.ImageUrl,
              );
              break;
            }
          }
        } catch (portfolioError) {
          handleError({
            message: "Could not load tour details. Please refresh the page.",
            detail: portfolioError.message,
            step: "tour_details",
          });
        }

        setError(null);
        setShowError(false);
        break;
      }

      if (!found) {
        handleError({
          message:
            "Payment information not found or payment already processed. Please check your tour status.",
          detail: `Payment ID: ${tourId}`,
          step: "payment_lookup",
        });
      }
    } catch (loadError) {
      handleError({
        message: "Could not load payment information. Please refresh the page.",
        detail: loadError.message,
        step: "initial_load",
      });
    } finally {
      setLoading(false);
    }
  }, [handleError]);

  useEffect(() => {
    if (didInitRef.current) {
      return;
    }
    didInitRef.current = true;

    if (!validateOrigin(window.location.origin)) {
      handleError({
        message: "Invalid request origin.",
        step: "security_validation",
      });
      return;
    }

    const startTime = Date.now();
    getTourInformation();

    trackPerformanceMetric("form_load_time", Date.now() - startTime, {
      tourId: getQueryVariable("TourId"),
    });

    trackCheckoutStep("form_loaded", {
      tourId: getQueryVariable("TourId"),
      isLiveMode: getLiveMode(),
    });
  }, [getTourInformation, handleError]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    const startTime = Date.now();
    trackCheckoutStep("payment_initiated", {
      tourName: nameForTour,
      amount: priceForTour,
    });

    if (!validateInputs()) {
      return;
    }

    if (!stripe || !elements) {
      handleError({
        message: "Payment system is still loading. Please wait a moment.",
        detail: "Please wait a moment and try again.",
        step: "initialization",
      });
      return;
    }

    if (error) {
      elements.getElement("card").focus();
      return;
    }

    if (!cardComplete) {
      handleError({
        message: "Please complete all card details before submitting.",
        detail: "Make sure to fill in your complete card information.",
        step: "validation",
      });
      return;
    }

    setProcessing(true);
    setError(null);

    try {
      const paymentMethodResult = await stripe.createPaymentMethod({
        type: "card",
        card: elements.getElement(CardElement),
        billing_details: {
          ...billingDetails,
          name: sanitizeInput(billingDetails.name),
          email: sanitizeInput(billingDetails.email),
        },
      });

      if (paymentMethodResult.error) {
        handleError({
          ...paymentMethodResult.error,
          step: "payment_method_creation",
        });
        return;
      }

      const tourId = getQueryVariable("TourId");
      const isLiveMode = getLiveMode();

      const paymentIntentResult = await createPaymentIntent({
        tourId,
        paymentMethodId: paymentMethodResult.paymentMethod.id,
        isLiveMode,
        nonce,
      });

      const paymentData = paymentIntentResult.data;

      if (paymentData.detailedErrorCode || paymentData.status !== "OK") {
        handleError({
          message:
            paymentData.message ||
            paymentData.detailedErrorCode ||
            "Payment initialization failed",
          detail: "If this continues, please try a different payment method.",
          step: "payment_intent_creation",
        });
        return;
      }

      if (paymentData.requiresAction) {
        const confirmPaymentResult = await stripe.confirmCardPayment(
          paymentData.clientSecret,
          {
            payment_method: {
              card: elements.getElement(CardElement),
              billing_details: {
                name: sanitizeInput(billingDetails.name),
                email: sanitizeInput(billingDetails.email),
              },
            },
          },
        );

        if (confirmPaymentResult.error) {
          handleError({
            ...confirmPaymentResult.error,
            step: "payment_authentication",
          });
          return;
        }

        if (confirmPaymentResult.paymentIntent.status !== "succeeded") {
          handleError({
            message:
              confirmPaymentResult.error?.message ||
              "Payment authentication failed",
            detail:
              "Please verify your card information or try a different payment method.",
            step: "payment_confirmation",
          });
          return;
        }
      }

      let authResult;
      try {
        authResult = await confirmAuthorizationCompletion({
          tourId,
          paymentIntentId: paymentData.paymentIntentId,
          isLiveMode,
          nonce,
        });
      } catch (authError) {
        handleError({
          message: "Failed to complete payment authorization",
          detail: authError.message || "Please try again or contact support",
          step: "payment_completion",
          code: "AUTH_ERROR",
        });
        return;
      }

      const authResponse = authResult?.data?.data || authResult?.data;
      if (!authResponse || authResponse.status !== "OK") {
        handleError({
          message: "Payment completion failed",
          detail: `Please try again or contact support if the problem persists. ${
            authResponse?.detailedErrorCode
              ? `Error: ${authResponse.detailedErrorCode}`
              : ""
          }`,
          step: "payment_completion",
          code: authResponse?.detailedErrorCode || "UNKNOWN_ERROR",
        });
        return;
      }

      trackPaymentSuccess(priceForTour, "PLN", "card", {
        tourName: nameForTour,
        processingTime: Date.now() - startTime,
        tourId,
      });

      setProcessing(false);
      setIsFinished(true);
      setShowError(false);
      setError(null);
    } catch (submitError) {
      handleError(submitError);
      setProcessing(false);
    }
  };

  if (loading) {
    return <LoadingIndicator text="Loading tour details..." />;
  }

  if (nameForTour == null) {
    return (
      <div className="PaymentStateCard PaymentStateCard--error">
        <h2>This payment link is no longer available</h2>
        <p>The link you opened is invalid or has already expired.</p>
      </div>
    );
  }

  return finished ? (
    <div className="PaymentFlowShell">
      <section className="PaymentHeroCard PaymentHeroCard--success">
        <div className="HeroEyebrow">Payment complete</div>
        <div className="ResultTitle" role="alert">
          Your tour is confirmed
        </div>
        <div className="ResultMessage">
          <p>Payment received successfully.</p>
          <p>Your booking is ready to review in GuideHub.</p>
        </div>
        <div className="HeroStatRow">
          <div className="HeroStat">
            <span className="HeroStatLabel">Tour</span>
            <strong>{nameForTour}</strong>
          </div>
          <div className="HeroStat">
            <span className="HeroStatLabel">Total paid</span>
            <strong>{priceForTour} PLN</strong>
          </div>
        </div>
      </section>

      <div className="Result Result--success">
        <div className="TourImageContainer">
          <TourPreview tourImagePath={tourImagePath} tourName={nameForTour} />
        </div>
        <div className="ResultMessage ResultMessage--compact">
          <p>Your payment has been linked to the booking automatically.</p>
          <p>Open the booking to see the latest status and next steps.</p>
        </div>
        <ResetButton
          onClick={() =>
            window.open(`https://app.guidehub.eu/#/tour/${getQueryVariable("TourId")}`)
          }
          buttonText="View tour details"
          className="ResetButton--primary"
        />
      </div>
    </div>
  ) : (
    <>
      <ErrorPopup
        error={error}
        show={showError}
        onClose={() => {
          setShowError(false);
          setError(null);
        }}
      />
      <div className="PaymentFlowShell">
        <CheckoutHero
          nameForTour={nameForTour}
          priceForTour={priceForTour}
          tourImagePath={tourImagePath}
        />

        <form className="Form" onSubmit={handleSubmit}>
          <div className="FormCardHeader">
            <div>
              <div className="FormCardEyebrow">Secure payment</div>
              <div className="ResultTitle" role="alert">
                Pay for your {nameForTour} tour
              </div>
            </div>
            <div className="FormHeaderAmount">
              <span>Total due</span>
              <strong>{priceForTour} PLN</strong>
            </div>
          </div>

          <div className="PaymentMiniSummary">
            <div className="PaymentMiniSummaryRow">
              <span>Tour</span>
              <strong>{nameForTour}</strong>
            </div>
            <div className="PaymentMiniSummaryRow">
              <span>Payment method</span>
              <strong>Card checkout</strong>
            </div>
          </div>

          <fieldset className="FormGroup">
            <Field
              label="Name"
              id="name"
              type="text"
              placeholder="Jane Doe"
              required
              autoComplete="name"
              value={billingDetails.name}
              onChange={(e) => {
                const sanitizedValue = sanitizeInput(e.target.value);
                setBillingDetails({ ...billingDetails, name: sanitizedValue });
                trackFormInteraction("name", "input");
              }}
              maxLength={50}
            />
            <Field
              label="Email"
              id="email"
              type="email"
              placeholder="janedoe@gmail.com"
              required
              autoComplete="email"
              value={billingDetails.email}
              onChange={(e) => {
                const sanitizedValue = sanitizeInput(e.target.value);
                setBillingDetails({ ...billingDetails, email: sanitizedValue });
                trackFormInteraction("email", "input");
              }}
              maxLength={100}
            />
          </fieldset>
          <fieldset className="FormGroup">
            <CardField
              onChange={(e) => {
                if (e.error) {
                  setError({
                    message: e.error.message,
                    isValidationError: true,
                  });
                } else {
                  setError(null);
                }
                setCardComplete(e.complete);
                trackFormInteraction("card", e.error ? "error" : "input");
              }}
            />
          </fieldset>

          {error && !showError && <ErrorMessage>{error.message}</ErrorMessage>}
          <SubmitButton
            processing={processing}
            error={error}
            disabled={!stripe || processing}
          >
            Pay {priceForTour} PLN
          </SubmitButton>

          <div className="PaymentGuarantee">
            <p>Your payment is protected by a secure checkout flow.</p>
            <p>If your bank asks for verification, complete it and return here.</p>
            <p>Booking confirmation appears immediately after payment.</p>
          </div>
        </form>
      </div>
    </>
  );
};
